import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  Avatar,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Divider,
  useTheme,
  CircularProgress,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PhoneIcon from "@mui/icons-material/Phone";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ChatThread as ChatThreadType, ChatMessage } from "../types";
import { MessageBubble } from "./MessageBubble";
import { ChatComposer } from "./ChatComposer";
import { format, isToday, isYesterday } from "date-fns";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Fade from "@mui/material/Fade";

interface ChatThreadProps {
  thread: ChatThreadType | null;
  messages: ChatMessage[];
  loadingMessages: boolean;
  sending: boolean;
  onSendMessage: (text: string) => Promise<void>;
  onOpenTemplateModal: () => void;
  onRefreshMessages: () => void;
  onBackToList?: () => void;
  isMobile?: boolean;
}

const getAvatarGradient = (str: string) => {
  const gradients = [
    "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
    "linear-gradient(135deg, #ec4899 0%, #be185d 100%)",
    "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    "linear-gradient(135deg, #10b981 0%, #047857 100%)",
    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return gradients[Math.abs(hash) % gradients.length];
};

const getInitials = (name: string) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const formatDateDivider = (dateStr: string) => {
  try {
    const d = new Date(dateStr);
    if (isToday(d)) return "Today";
    if (isYesterday(d)) return "Yesterday";
    return format(d, "EEEE, d MMMM yyyy");
  } catch {
    return "";
  }
};

export const ChatThread: React.FC<ChatThreadProps> = ({
  thread,
  messages,
  loadingMessages,
  sending,
  onSendMessage,
  onOpenTemplateModal,
  onRefreshMessages,
  onBackToList,
  isMobile = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const scrollRef = useRef<HTMLDivElement>(null);

  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const prevRecipientRef = useRef<string | null>(null);
  const prevMessagesCountRef = useRef<number>(0);
  const isNearBottomRef = useRef<boolean>(true);

  // Monitor user scrolling to detect if user has scrolled away from the bottom
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const nearBottom = distanceFromBottom <= 120;
    isNearBottomRef.current = nearBottom;
    setShowScrollBottom(!nearBottom && scrollHeight > clientHeight + 100);
  };

  const scrollToBottom = (smooth = false) => {
    if (scrollRef.current) {
      if (smooth) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "smooth",
        });
      } else {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
      isNearBottomRef.current = true;
      setShowScrollBottom(false);
    }
  };

  // Smart auto-scroll:
  // 1. Thread changed -> always scroll to bottom
  // 2. User was already near bottom when new messages arrived -> scroll to bottom
  // 3. User is scrolled up reading history -> DO NOT JUMP / interrupt scroll position
  useEffect(() => {
    const threadChanged = thread?.recipient !== prevRecipientRef.current;
    if (threadChanged) {
      prevRecipientRef.current = thread?.recipient || null;
      prevMessagesCountRef.current = messages.length;
      setTimeout(() => scrollToBottom(false), 50);
      return;
    }

    const newMessagesCount = messages.length;
    const countIncreased = newMessagesCount > prevMessagesCountRef.current;
    prevMessagesCountRef.current = newMessagesCount;

    // Only scroll to bottom if user is already at the bottom or sent a message
    if (isNearBottomRef.current || (countIncreased && sending)) {
      scrollToBottom(false);
    }
  }, [messages, thread?.recipient, sending]);

  if (!thread) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          p: 4,
          textAlign: "center",
          backgroundColor: isDark ? "#0f172a" : "#F8FAFC",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "rgba(37, 211, 102, 0.12)",
            color: "#25D366",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
            boxShadow: "0 8px 24px rgba(37, 211, 102, 0.2)",
            border: "1px solid rgba(37, 211, 102, 0.25)",
          }}
        >
          <WhatsAppIcon sx={{ fontSize: 44 }} />
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, mb: 1, color: isDark ? "#FFFFFF" : "#0F172A" }}
        >
          WhatsApp Customer Hub
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: isDark ? "#94A3B8" : "#64748B", maxWidth: 440, lineHeight: 1.6 }}
        >
          Select a customer from the left to view the complete WhatsApp conversation,
          delivery statuses, and send direct replies or templates via Meta Cloud API.
        </Typography>
      </Box>
    );
  }

  // Group messages by date
  const groupedMessages: { date: string; items: ChatMessage[] }[] = [];
  messages.forEach((msg) => {
    const dateKey = formatDateDivider(msg.created_at);
    const existing = groupedMessages.find((g) => g.date === dateKey);
    if (existing) {
      existing.items.push(msg);
    } else {
      groupedMessages.push({ date: dateKey, items: [msg] });
    }
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: isDark ? "#0b1120" : "#F0F4F8",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          px: 3,
          backgroundColor: isDark ? "#111827" : "#FFFFFF",
          borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          {isMobile && (
            <IconButton onClick={onBackToList} size="small" sx={{ mr: 0.5, color: isDark ? "#FFFFFF" : "#0F172A" }}>
              <ArrowBackIcon />
            </IconButton>
          )}

          <Avatar
            sx={{
              width: 48,
              height: 48,
              background: getAvatarGradient(thread.contact_name || thread.recipient),
              fontWeight: 700,
              fontSize: "1.05rem",
              color: "#FFFFFF",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            {getInitials(thread.contact_name || thread.recipient)}
          </Avatar>

          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: isDark ? "#FFFFFF" : "#0F172A",
                  fontSize: "1.05rem",
                }}
              >
                {thread.contact_name || thread.formatted_phone}
              </Typography>

              {thread.is_window_open ? (
                <Chip
                  label="24h Window Active"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    backgroundColor: "rgba(37, 211, 102, 0.12)",
                    color: "#16a34a",
                    border: "1px solid rgba(37, 211, 102, 0.3)",
                  }}
                />
              ) : (
                <Chip
                  label="Template Required"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    backgroundColor: isDark ? "rgba(245, 158, 11, 0.15)" : "#FEF3C7",
                    color: isDark ? "#fcd34d" : "#b45309",
                    border: "1px solid rgba(245, 158, 11, 0.25)",
                  }}
                />
              )}
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.2 }}>
              <Typography variant="caption" sx={{ color: isDark ? "#94A3B8" : "#64748B" }}>
                {thread.formatted_phone}
              </Typography>

              {thread.booking_title && (
                <Tooltip title="Linked MMS Booking">
                  <Chip
                    icon={<EventNoteIcon sx={{ fontSize: "12px !important", color: isDark ? "#fbbf24 !important" : "#b45309 !important" }} />}
                    label={thread.booking_title}
                    size="small"
                    component="a"
                    href={`/bookings/view-booking/${thread.booking_id}`}
                    target="_blank"
                    clickable
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      backgroundColor: isDark ? "rgba(251, 191, 36, 0.12)" : "#FEF3C7",
                      color: isDark ? "#fbbf24" : "#b45309",
                      border: "1px solid rgba(251, 191, 36, 0.25)",
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Box>
        </Stack>

        {/* Top Right Action Buttons */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={onOpenTemplateModal}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: "16px !important" }} />}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
              borderColor: "#724E41",
              color: "#724E41",
              backgroundColor: isDark ? "rgba(114, 78, 65, 0.2)" : "rgba(114, 78, 65, 0.05)",
              px: 2,
              "&:hover": {
                backgroundColor: "#724E41",
                color: "#FFFFFF",
                borderColor: "#724E41",
              },
            }}
          >
            Send Template
          </Button>

          <Tooltip title="Refresh messages">
            <IconButton
              size="small"
              onClick={onRefreshMessages}
              disabled={loadingMessages}
              sx={{
                color: isDark ? "#94A3B8" : "#64748B",
                "&:hover": { color: "#724E41" },
              }}
            >
              {loadingMessages ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Messages Scrollable Area with Floating Scroll Button */}
      <Box sx={{ position: "relative", flexGrow: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: 3,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loadingMessages && messages.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={32} sx={{ color: "#724E41" }} />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="body2" sx={{ color: isDark ? "#94A3B8" : "#64748B" }}>
                No messages found for this customer. Send a template or text to start!
              </Typography>
            </Box>
          ) : (
            groupedMessages.map((group) => (
              <React.Fragment key={group.date}>
                {/* Date Divider */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    my: 2.5,
                  }}
                >
                  <Chip
                    label={group.date}
                    size="small"
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#E2E8F0",
                      color: isDark ? "#94A3B8" : "#475569",
                      border: "1px solid",
                      borderColor: isDark ? "rgba(255, 255, 255, 0.06)" : "#CBD5E1",
                    }}
                  />
                </Box>

                {/* Message bubbles */}
                {group.items.map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    contactName={thread.contact_name}
                  />
                ))}
              </React.Fragment>
            ))
          )}
        </Box>

        {/* Floating Scroll to Bottom Button */}
        <Fade in={showScrollBottom}>
          <Box sx={{ position: "absolute", bottom: 16, right: 28, zIndex: 10 }}>
            <Tooltip title="Scroll to latest messages">
              <IconButton
                size="small"
                onClick={() => scrollToBottom(true)}
                sx={{
                  width: 38,
                  height: 38,
                  backgroundColor: isDark ? "#1E293B" : "#FFFFFF",
                  color: isDark ? "#F8FAFC" : "#0F172A",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.2)",
                  border: isDark ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid #CBD5E1",
                  "&:hover": {
                    backgroundColor: isDark ? "#334155" : "#F1F5F9",
                    color: "#25D366",
                  },
                }}
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Fade>
      </Box>

      {/* Composer */}
      <ChatComposer
        isWindowOpen={thread.is_window_open}
        windowExpiresAt={thread.window_expires_at}
        onSendMessage={onSendMessage}
        onOpenTemplateModal={onOpenTemplateModal}
        sending={sending}
      />
    </Box>
  );
};
